import React from 'react';
import { HeroSection } from '@/components/landing/HeroSection';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Map, ArrowRight, Calculator, PenTool, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const HomePage: React.FC = () => {
  const siteUrl = import.meta.env.VITE_APP_URL || 'https://runofftrack.com';
  
  const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "RunOffTrack",
      "url": siteUrl,
      "description": "SWPPP inspection software for consultants: manage sites, record findings, and generate signed PDF inspection reports."
  };

  const tools = [
    {
        icon: ClipboardCheck, title: "Sample Inspection", desc: "Try the field workflow before creating an account.", link: "/demo/sample-inspection"
    },
    {
        icon: Map, title: "State Directory", desc: "Permit links and inspection references for all 50 states.", link: "/swppp-requirements"
    },
    { 
        icon: PenTool, title: "Map Sketcher", desc: "Mark BMPs and organize a clear field-ready site plan.", link: "/tools/map-sketcher"
    },
    { 
        icon: BookOpen, title: "BMP Field Guide", desc: "Spot common failures and understand maintenance cues.", link: "/resources/bmp-field-guide"
    },
    { 
        icon: FileText, title: "Free Templates", desc: "Practical inspection logs and corrective-action forms.", link: "/resources/templates"
    },
    { 
        icon: Calculator, title: "Fine Calculator", desc: "Explore potential exposure with a simple scenario tool.", link: "/tools/fine-calculator"
    },
  ];

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-blue-500/30">
      <SEO 
        title="SWPPP Inspection Software for Consultants"
        description="Manage construction sites, record field findings, and generate signed PDF inspection reports. Start with one site free; Pro covers 5 active sites for $49/month."
        canonical="/"
        jsonLd={jsonLd}
      />

      <HeroSection />
      
      {/* Resources Hub Section */}
      <section className="bg-background py-20 md:py-28">
        <div className="container px-4 max-w-6xl mx-auto">
            <div className="mb-12 max-w-2xl">
                <p className="section-label mb-3">Practical resources</p>
                <h2 className="mb-4 text-3xl font-black tracking-tight md:text-5xl">Get to the answer faster.</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">Use focused references built around the questions that come up on active construction sites.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tools.map((tool) => (
                    <Link key={tool.link} to={tool.link} className="group block h-full rounded-2xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4">
                    <Card className="h-full overflow-hidden transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-lg">
                        <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                                <tool.icon size={24} />
                            </div>
                            <div>
                                <CardTitle className="text-base group-hover:text-primary transition-colors">{tool.title}</CardTitle>
                                <CardDescription className="mt-1">
                                    {tool.desc}
                                </CardDescription>
                            </div>
                        </CardHeader>
                        <CardContent className="mt-auto">
                            <div className="flex items-center gap-2 text-sm font-bold text-primary">
                                Open resource <ArrowRight size={14} className="transition-transform group-hover:translate-x-1"/>
                            </div>
                        </CardContent>
                    </Card>
                    </Link>
                ))}
            </div>
            
            <div className="mt-12 text-center">
                 <Button variant="outline" asChild>
                     <Link to="/resources/toolbox-talks">View toolbox talks</Link>
                 </Button>
            </div>
        </div>
      </section>

      {/* Trust / Closing Section */}
      <section className="border-y border-border/70 bg-secondary/45 py-20 md:py-24">
       <div className="container px-4 text-center">
         <p className="section-label mb-3">Built for useful work</p>
         <h2 className="mb-5 text-3xl font-black tracking-tight text-foreground md:text-5xl">Managing inspections for several clients?</h2>
         <p className="mx-auto mb-9 max-w-2xl text-lg leading-relaxed text-muted-foreground">
           See how the inspection workflow fits your firm, then choose a plan for the number of active sites you manage.
         </p>
         <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button size="lg" asChild>
                <Link to="/solutions/for-consultants">Explore the consultant workflow</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
                <Link to="/pricing">Compare plans</Link>
            </Button>
         </div>
       </div>
      </section>

    </div>
  );
};

export default HomePage;
